import { useQuery } from '@apollo/client'
import { ReactElement, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import { Autocomplete } from 'components/v2/Autocomplete'
import { GTSalesChannelsOutput } from 'gql/types.generated'
import { GET_SALES_CHANNELS } from 'features/Services/queries-legacy'
import { ISelectSalesChannelV2Props } from 'features/Services/components/SelectSalesChannelV2/types'
import { ISalesChannel } from 'features/Services/components/NewServicesV2/components/OfferSettingsCard/types'
import { isEmpty } from 'lodash'
import { normalize } from 'utils/string'

const messages = defineMessages({
  placeholder: {
    id: 'selectSalesChannel.autocomplete.placeholder',
    defaultMessage: 'Select a sales channel'
  },
  empty: {
    id: 'selectSalesChannel.autocomplete.empty',
    defaultMessage: 'No sales channels found'
  },
  error: {
    id: 'selectSalesChannel.autocomplete.error',
    defaultMessage: 'There was an issue while loading your sales channels'
  }
})

export const SelectSalesChannelV2 = ({
  countryId,
  value,
  label,
  onChange,
  errors,
  touched
}: ISelectSalesChannelV2Props): ReactElement => {
  const intl = useIntl()
  const [pattern, setPattern] = useState('')
  const { data, loading, error } = useQuery<{ salesChannels: GTSalesChannelsOutput }>(GET_SALES_CHANNELS, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      pagination: {
        page: 0,
        size: 10000
      },
      input: {
        countryID: countryId
      }
    }
  })

  const handleChange = (salesChannel: ISalesChannel, { inputValue = '' } = {}): void => {
    setPattern(inputValue)
    if (!isEmpty(salesChannel)) {
      onChange(salesChannel)
    } else {
      onChange({
        id: '',
        name: ''
      })
    }
  }

  const shownItems =
    data?.salesChannels.items.filter((salesChannel) => {
      return normalize(salesChannel.name).indexOf(normalize(pattern)) > -1
    }) || []

  return (
    <Autocomplete
      placeholder={intl.formatMessage(messages.placeholder)}
      value={value?.name ?? ''}
      label={label}
      items={shownItems}
      getItemLabel={(salesChannel: ISalesChannel): string => salesChannel.name}
      loading={loading}
      error={error}
      errorMessage={intl.formatMessage(messages.error)}
      emptyMessage={intl.formatMessage(messages.empty)}
      onChange={handleChange}
      errors={errors}
      touched={touched}
    />
  )
}
